import React, { useState, useEffect } from "react";
import { Nav, Accordion } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaHome,
  FaSignOutAlt,
  FaUsers,
  FaFileInvoice,
  FaMoneyCheckAlt,
  FaHandshake,
  FaBuilding,
  FaUserTie,
  FaBox,
  FaBoxes,
  FaRupeeSign,
  FaChartBar,
  FaSave,
  FaUserShield,
  FaShoppingCart,
  FaSitemap,
  FaTable,
} from "react-icons/fa";

import "../styles/Navigation.css";

const MOBILE_BREAKPOINT = 768;

const Navigation = ({
  isOpen = true,
  userPermissions = [],
  featureAccessMap = {},
  closeSidebar,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Determine active route
  const currentPath = location.pathname;

  const handleNavigate = (route) => {
    navigate(route);
    // Only auto-close on mobile/tablet, where the sidebar is an overlay drawer.
    // On desktop it's a persistent rail (isOpen just toggles expanded/collapsed width).
    if (window.innerWidth < MOBILE_BREAKPOINT && typeof closeSidebar === "function") {
      closeSidebar();
    }
  };

  const handleGroupToggle = (group) => {
    setExpandedGroup(expandedGroup === group ? null : group);
  };

  // Helper route matching functions
  const isRouteActive = (route, exact = false) => {
    if (exact || route === "/") {
      return currentPath === route;
    }
    return currentPath.startsWith(route);
  };

  const salesRoutes = [
    "/invoice",
    "/invoices",
    "/generateinvoice",
    "/payment",
    "/retailers",
  ];
  const invRoutes = ["/items", "/expenses", "/dailysalesrecords"];
  const mktRoutes = ["/marketplaceorders", "/productmappings"];
  const orgRoutes = [
    "/company",
    "/employees",
    "/getallemployees",
    "/products",
    "/users",
    "/update-user",
    "/roles",
  ];

  const isSalesGroupActive = salesRoutes.some((route) =>
    currentPath.startsWith(route)
  );
  const isInvGroupActive = invRoutes.some((route) =>
    currentPath.startsWith(route)
  );
  const isMktGroupActive = mktRoutes.some((route) =>
    currentPath.startsWith(route)
  );
  const isOrgGroupActive = orgRoutes.some((route) =>
    currentPath.startsWith(route)
  );

  // Auto expand group based on active path
  useEffect(() => {
    if (isOrgGroupActive) {
      setExpandedGroup("org");
    } else if (isSalesGroupActive) {
      setExpandedGroup("sales");
    } else if (isMktGroupActive) {
      setExpandedGroup("mkt");
    } else if (isInvGroupActive) {
      setExpandedGroup("inv");
    }
  }, [
    currentPath,
    isOrgGroupActive,
    isSalesGroupActive,
    isMktGroupActive,
    isInvGroupActive,
  ]);

  return (
    <aside className={`side-navbar ${isOpen ? "open" : "closed"}`}>
      <div className="nav-scroll-container">
        <Nav className="flex-column">
          {/* Main Section */}
          {isOpen && <div className="nav-section-title">Main</div>}

          <Nav.Link
            onClick={() => handleNavigate("/")}
            className={isRouteActive("/", true) ? "active" : ""}
            title="Dashboard Overview"
          >
            <FaHome /> {isOpen && <span>Dashboard</span>}
          </Nav.Link>

          <Nav.Link
            onClick={() => handleNavigate("/customers")}
            className={isRouteActive("/customers") ? "active" : ""}
            title="Customers"
          >
            <FaUsers /> {isOpen && <span>Customers</span>}
          </Nav.Link>

          <Nav.Link
            onClick={() => handleNavigate("/reports")}
            className={isRouteActive("/reports") ? "active" : ""}
            title="Analytics & Reports"
          >
            <FaChartBar /> {isOpen && <span>Reports</span>}
          </Nav.Link>

          <Nav.Link
            onClick={() => handleNavigate("/sales-matrix")}
            className={isRouteActive("/sales-matrix") ? "active" : ""}
            title="Product Sales Matrix"
          >
            <FaTable /> {isOpen && <span>Sales Matrix</span>}
          </Nav.Link>

          {/* Grouped Modules */}
          {isOpen && <div className="nav-section-title mt-2">Modules</div>}

          <Accordion activeKey={expandedGroup} flush>
            {/* Sales & Billing */}
            <Accordion.Item
              eventKey="sales"
              className={isSalesGroupActive ? "group-active" : ""}
            >
              <Accordion.Header onClick={() => handleGroupToggle("sales")}>
                <FaFileInvoice /> {isOpen && <span>Sales & Billing</span>}
              </Accordion.Header>
              <Accordion.Body>
                <Nav.Link
                  onClick={() => handleNavigate("/invoice")}
                  className={
                    isRouteActive("/invoice") ||
                    isRouteActive("/invoices") ||
                    isRouteActive("/generateinvoice")
                      ? "active"
                      : ""
                  }
                >
                  <FaFileInvoice /> {isOpen && <span>Invoices</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/payment")}
                  className={isRouteActive("/payment") ? "active" : ""}
                >
                  <FaMoneyCheckAlt /> {isOpen && <span>Payments</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/retailers")}
                  className={isRouteActive("/retailers") ? "active" : ""}
                >
                  <FaHandshake /> {isOpen && <span>Retailers</span>}
                </Nav.Link>
              </Accordion.Body>
            </Accordion.Item>

            {/* Inventory & Expenses */}
            <Accordion.Item
              eventKey="inv"
              className={isInvGroupActive ? "group-active" : ""}
            >
              <Accordion.Header onClick={() => handleGroupToggle("inv")}>
                <FaBoxes /> {isOpen && <span>Inventory & Expenses</span>}
              </Accordion.Header>
              <Accordion.Body>
                <Nav.Link
                  onClick={() => handleNavigate("/items")}
                  className={isRouteActive("/items") ? "active" : ""}
                >
                  <FaBox /> {isOpen && <span>Items</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/expenses")}
                  className={isRouteActive("/expenses") ? "active" : ""}
                >
                  <FaRupeeSign /> {isOpen && <span>Expenses</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/dailysalesrecords")}
                  className={
                    isRouteActive("/dailysalesrecords") ? "active" : ""
                  }
                >
                  <FaSave /> {isOpen && <span>Daily Sales</span>}
                </Nav.Link>
              </Accordion.Body>
            </Accordion.Item>

            {/* Marketplace Module */}
            <Accordion.Item
              eventKey="mkt"
              className={isMktGroupActive ? "group-active" : ""}
            >
              <Accordion.Header onClick={() => handleGroupToggle("mkt")}>
                <FaShoppingCart /> {isOpen && <span>Marketplace</span>}
              </Accordion.Header>
              <Accordion.Body>
                <Nav.Link
                  onClick={() => handleNavigate("/marketplaceorders")}
                  className={
                    isRouteActive("/marketplaceorders") ? "active" : ""
                  }
                >
                  <FaShoppingCart />{" "}
                  {isOpen && <span>Marketplace Orders</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/productmappings")}
                  className={isRouteActive("/productmappings") ? "active" : ""}
                >
                  <FaSitemap /> {isOpen && <span>Product Mappings</span>}
                </Nav.Link>
              </Accordion.Body>
            </Accordion.Item>

            {/* Organization & HR */}
            <Accordion.Item
              eventKey="org"
              className={isOrgGroupActive ? "group-active" : ""}
            >
              <Accordion.Header onClick={() => handleGroupToggle("org")}>
                <FaBuilding /> {isOpen && <span>Organization</span>}
              </Accordion.Header>
              <Accordion.Body>
                <Nav.Link
                  onClick={() => handleNavigate("/company")}
                  className={isRouteActive("/company") ? "active" : ""}
                >
                  <FaBuilding /> {isOpen && <span>Company</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/employees")}
                  className={
                    isRouteActive("/employees") ||
                    isRouteActive("/getallemployees")
                      ? "active"
                      : ""
                  }
                >
                  <FaUserTie /> {isOpen && <span>Employees</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/products")}
                  className={isRouteActive("/products") ? "active" : ""}
                >
                  <FaBox /> {isOpen && <span>Products</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/users")}
                  className={
                    isRouteActive("/users") || isRouteActive("/update-user")
                      ? "active"
                      : ""
                  }
                >
                  <FaUsers /> {isOpen && <span>Users</span>}
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleNavigate("/roles")}
                  className={isRouteActive("/roles") ? "active" : ""}
                >
                  <FaUserShield /> {isOpen && <span>Roles</span>}
                </Nav.Link>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Nav>
      </div>

      {isOpen && (
        <div className="sidebar-footer">
          <img
            src="/images/Logo1.png"
            alt="Soochi"
            className="sidebar-logo"
          />
          <small className="text-muted">Version 1.0</small>
        </div>
      )}
    </aside>
  );
};

export default Navigation;
